/**
 * print/PrintCanvas — the print-safe page primitive.
 *
 * What it is:
 *   • A single SVG that lays out the bleed / trim / safe rectangles
 *     for a PrintFormat in true millimetre space.
 *   • A frame for any artifact composition (children) that draws
 *     itself in mm against the canvas viewBox.
 *   • A preview-mode overlay (`showMarks`) that surfaces hairline
 *     trim and safe-margin guides — visible to the studio host,
 *     NEVER baked into the exported file.
 *
 * What it is NOT:
 *   • A button container.
 *   • A toolbar host.
 *   • A frame for any interactive affordance.
 *
 * Print artifacts must never inherit live UI chrome. The canvas
 * children should render as a designed object, not a screenshot of
 * a product. No gradients-of-the-app, no SaaS residue, no AI sheen.
 */
import React from 'react';
import { rects, previewScale } from '../../lib/print/printGeometry';

// Marks colour — a 25% gray hairline. Off-press it reads as a
// gentle guide; in preview it never competes with the artifact.
const MARK_COLOUR = 'rgba(0, 0, 0, 0.18)';
const MARK_COLOUR_SAFE = 'rgba(184, 151, 98, 0.45)'; // restrained gold

const PrintCanvas = ({
  format,
  maxWidthPx = 520,
  showMarks = true,
  background = '#F4ECDE', // parchment edge — never pure white in preview
  children,
  testId = 'print-canvas',
}) => {
  if (!format) return null;
  const { bleedRect, trimRect, safeRect } = rects(format);
  const pxPerMm = previewScale(format, maxWidthPx);
  const cssWidth  = bleedRect.w * pxPerMm;
  const cssHeight = bleedRect.h * pxPerMm;

  return (
    <div
      data-testid={testId}
      data-format={format.id}
      style={{
        display: 'inline-block',
        width: cssWidth, height: cssHeight,
        // Print-safe drop shadow that signals "this is paper", not
        // "this is a card UI element". Soft, off-axis, archival.
        filter: 'drop-shadow(0 18px 28px rgba(0,0,0,0.22))',
      }}
    >
      <svg
        viewBox={`0 0 ${bleedRect.w} ${bleedRect.h}`}
        width="100%"
        height="100%"
        style={{ display: 'block' }}
      >
        {/* Bleed page — the actual paper. */}
        <rect
          x={bleedRect.x} y={bleedRect.y}
          width={bleedRect.w} height={bleedRect.h}
          fill={background}
        />

        {/* The artifact composition draws itself inside the bleed
            rectangle. The child component is responsible for staying
            *inside* the safe rectangle for anything critical (text,
            faces). The geometry is exposed via the format prop. */}
        <g data-testid="print-canvas-content">
          {typeof children === 'function'
            ? children({ trimRect, safeRect, bleedRect, format })
            : children}
        </g>

        {/* Preview-only guides. Studio host sees the trim and safe
            rectangles; the server export pipeline never renders
            these — they're a preview affordance, not a print artifact. */}
        {showMarks && (
          <g data-testid="print-canvas-marks" pointerEvents="none">
            {/* Trim rectangle — solid hairline */}
            <rect
              x={trimRect.x} y={trimRect.y}
              width={trimRect.w} height={trimRect.h}
              fill="none"
              stroke={MARK_COLOUR}
              strokeWidth={0.15}
            />
            {/* Safe rectangle — dashed gold hairline */}
            <rect
              x={safeRect.x} y={safeRect.y}
              width={safeRect.w} height={safeRect.h}
              fill="none"
              stroke={MARK_COLOUR_SAFE}
              strokeWidth={0.12}
              strokeDasharray="0.8,0.8"
            />
            {/* Crop crosshairs at each corner of the trim — tiny
                press-style ticks, not full crop marks. They sit
                inside the bleed so they signal alignment without
                pretending to be camera-grade printer marks. */}
            {[
              [trimRect.x, trimRect.y],
              [trimRect.x + trimRect.w, trimRect.y],
              [trimRect.x, trimRect.y + trimRect.h],
              [trimRect.x + trimRect.w, trimRect.y + trimRect.h],
            ].map(([cx, cy], i) => (
              <g key={i}>
                <line x1={cx - 1.2} y1={cy} x2={cx + 1.2} y2={cy} stroke={MARK_COLOUR} strokeWidth={0.15} />
                <line x1={cx} y1={cy - 1.2} x2={cx} y2={cy + 1.2} stroke={MARK_COLOUR} strokeWidth={0.15} />
              </g>
            ))}
          </g>
        )}
      </svg>
    </div>
  );
};

export default PrintCanvas;
